import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormContext } from './FormContext';
import {
  AddressScreen,
  NamesScreen,
  PhoneScreen,
  ProgressBar,
} from './components';
import {
  CreateUserMutationVariables,
  useCreateUserMutation,
} from './createUser.generated';
import { IFormInput, SingleDetail } from './types';
import cslx from 'clsx';
import { useSetAtom } from 'jotai';
import { Button } from '@web/utils';
import { isUserRetreivedAtom } from '@/store/atoms';
import { Logo } from '../Logo';

const formDetails = {
  Names: [
    { id: 'givenName', required: true },
    { id: 'familyName', required: true },
  ],
  Phone: [{ id: 'phoneNumber', type: 'number', required: false }],
  Address: [
    { id: 'addressLine1', required: false },
    { id: 'addressLine2', required: false },
    { id: 'addressPostcode', required: false },
    { id: 'addressLocality', required: false },
  ],
};

export enum FormStage {
  Names = 'Names',
  Phone = 'Phone',
  Address = 'Address',
}

const initialValues = {
  givenName: '',
  familyName: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  addressPostcode: '',
  addressLocality: '',
  userMarketing: false,
  userTerms: false,
};

export default function CreateUserModal() {
  const [currentStage, setCurrentStage] = useState<FormStage>(FormStage.Names);
  const stagesOrder = [FormStage.Names, FormStage.Phone, FormStage.Address];
  const [isLoading, setLoading] = useState<boolean>(false);
  const [coordinates, setCoordinates] = useState({ lat: 0, long: 0 });
  const setUserRetreived = useSetAtom(isUserRetreivedAtom);
  const [form, setForm] = useState<IFormInput>({ ...initialValues });
  const [, createUser] = useCreateUserMutation();
  const { i18n, t } = useTranslation();

  const {
    register,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm<IFormInput>();

  const resetHandler = () => {
    setForm({ ...initialValues });
    clearErrors();
  };

  const inputHandler = (key: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValue(key as keyof SingleDetail as keyof IFormInput, value);
  };

  const handleFormSubmit: SubmitHandler<IFormInput> = async () => {
    setLoading(true);

    const user: CreateUserMutationVariables = {
      user: {
        familyName: form.familyName,
        givenName: form.givenName,
        phoneNumber: form.phoneNumber,
        language: i18n.language || 'de',
        emailMarketing: form.userMarketing,
        // userTerms: form.userTerms,
        primaryAddress: {
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2,
          addressPostcode: form.addressPostcode,
          addressLocality: form.addressLocality,
          coordinates,
        },
      },
    };
    try {
      const result = await createUser(user);
      if (result.error) {
        alert(result.error.message);
      } else {
        resetHandler();
        setUserRetreived(false);
        window.location.reload();
      }
    } catch (error) {
      console.log('error from mutation', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = (e, direction: 'next' | 'previous' = 'next') => {
    e.preventDefault();
    if (currentStage === FormStage.Address && direction === 'next') {
      handleFormSubmit(form);
    }
    const currentIndex = stagesOrder.indexOf(currentStage);
    if (direction === 'next' && currentIndex < stagesOrder.length - 1) {
      setCurrentStage(stagesOrder[currentIndex + 1]);
    } else if (direction === 'previous' && currentIndex > 0) {
      setCurrentStage(stagesOrder[currentIndex - 1]);
    }
  };

  const handleSkip = () => {
    if (currentStage === FormStage.Phone) {
      setCurrentStage(FormStage.Address);
    } else {
      handleFormSubmit(form);
    }
  };

  const isButtonDisabled = useMemo(() => {
    const isNameStageIncomplete =
      currentStage === FormStage.Names &&
      (!form.givenName || !form.familyName || !form.userTerms);

    return isLoading || isNameStageIncomplete;
  }, [
    currentStage,
    form.familyName,
    form.givenName,
    form.userTerms,
    isLoading,
  ]);

  const formContextValue = {
    form,
    inputHandler,
    register,
    errors,
    formDetails,
    currentStage,
    handleStageChange,
    setCoordinates,
  };

  return (
    <FormContext.Provider value={formContextValue}>
      <div
        className={cslx(
          'flex absolute top-0 left-0 z-100 min-h-100vh w-100-vw bg-grey-400 bg-opacity-40',
        )}
      >
        <form className="p-2-0 md:p-5-0 rounded-lg mx-auto my-auto w-full max-w-[500px] rounded-14-0 border border-grey-300 bg-white shadow md:min-h-[80vh] flex flex-col transition-transform duration-200">
          <div className="mb-1-6 flex justify-between items-center">
            <Logo type={2} />
            {currentStage !== FormStage.Names && (
              <span
                className="font-bd text-2-0 leading-1-0 text-primary-grey cursor-pointer"
                onClick={handleSkip}
                role="skip"
              >
                {t('global.skip')}
              </span>
            )}
          </div>
          <ProgressBar currentStage={currentStage} className="my-2-0" />
          {currentStage === FormStage.Names && <NamesScreen />}
          {currentStage === FormStage.Phone && <PhoneScreen />}
          {currentStage === FormStage.Address && <AddressScreen />}
          <div className="pt-4-0 mt-auto">
            <Button
              className="w-full"
              onClick={(e) => handleStageChange(e)}
              disabled={isButtonDisabled}
            >
              {t('global.next')}
            </Button>
          </div>
        </form>
      </div>
    </FormContext.Provider>
  );
}
